import { motion, AnimatePresence } from "framer-motion";
import { X, Star, ExternalLink } from "lucide-react";
import { useLang } from "../store";

const FORMAT_BADGE = { feature: "FILM", short: "SHORT", tv: "TV" };

export default function FilmModal({ film, onClose }) {
  const { lang, t } = useLang();
  return (
    <AnimatePresence>
      {film && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
          onClick={onClose}
          data-testid="film-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ type: "spring", damping: 26, stiffness: 260 }}
            onClick={(e) => e.stopPropagation()}
            className="grid max-h-[88vh] w-full max-w-3xl overflow-y-auto rounded-lg border border-white/10 bg-[#0e1014] md:grid-cols-[280px_1fr]"
          >
            <div className="relative bg-black">
              {film.poster ? (
                <img src={film.poster} alt={film.title} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full min-h-[300px] items-center justify-center bg-[#14181f] p-6">
                  <span className="font-display text-center text-2xl uppercase text-slate-500">{film.title}</span>
                </div>
              )}
              <span className="font-mono2 absolute left-3 top-3 rounded bg-black/80 px-2 py-1 text-[10px] tracking-widest text-amber-500">
                {FORMAT_BADGE[film.format]} · {film.year}
              </span>
            </div>
            <div className="p-7">
              <div className="flex items-start justify-between gap-4">
                <h3 className="font-display text-2xl uppercase leading-tight sm:text-3xl" data-testid="film-modal-title">{film.title}</h3>
                <button onClick={onClose} className="shrink-0 text-slate-500 hover:text-white" data-testid="film-modal-close"><X size={22} /></button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {film.roles.map((r) => (
                  <span key={r} className="font-mono2 rounded-full border border-amber-600/40 px-3 py-1 text-[10px] uppercase tracking-widest text-amber-500">
                    {t(`films.${r}`)}
                  </span>
                ))}
              </div>
              {film.character && (
                <p className="mt-4 text-sm text-slate-300">
                  <span className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("films.role")}: </span>
                  <span className="font-serif-it text-base italic">{film.character}</span>
                </p>
              )}
              {film.rating && (
                <p className="mt-2 flex items-center gap-1.5 text-sm text-slate-300">
                  <Star size={14} className="fill-amber-500 text-amber-500" /> {film.rating} <span className="text-xs text-slate-500">— {t("films.rating")}</span>
                </p>
              )}
              <p className="font-serif-it mt-5 text-lg italic leading-relaxed text-slate-300" data-testid="film-modal-synopsis">
                {lang === "it" ? film.synopsis_it : film.synopsis_en}
              </p>
              <a
                href={film.imdb}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="film-modal-imdb"
                className="mt-6 inline-flex items-center gap-2 rounded bg-amber-600 px-5 py-2.5 text-sm font-bold text-black transition-colors hover:bg-amber-500"
              >
                {t("films.imdbLink")} <ExternalLink size={14} />
              </a>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
